import { isBidStage } from '@/lib/bid-lifecycle'

// ===================
// Types
// ===================

export interface ActivityRecord {
  id: string
  organizationId?: string
  bidId?: string
  userId: string
  action: string
  targetType: string
  targetId: string | null
  targetName: string | null
  metadata: Record<string, unknown>
  createdAt: string
  userName: string | null
  userAvatarUrl: string | null
}

// ===================
// Action Labels
// ===================

/**
 * Third-person past-tense label for each action.
 * Covers the full ActivityAction enum — including reserved-but-unemitted AI-gen
 * events so the map is exhaustive.
 */
export const ACTION_LABELS: Record<string, string> = {
  // --- Membership ---
  member_added: 'added',
  member_removed: 'removed',
  ownership_transferred: 'became the owner',

  // --- Section collaboration ---
  section_assigned: 'was assigned',
  section_claimed: 'claimed',
  section_unassigned: 'unassigned',
  work_started: 'started working on',
  draft_completed: 'completed draft for',
  review_requested: 'requested review on',
  review_approved: 'approved',
  changes_requested: 'requested changes on',
  section_completed: 'completed',
  artifact_finalized: 'finalised',
  artifact_reopened: 'reopened',
  artifact_exported: 'exported',

  // --- Discussion ---
  comment_added: 'commented on',
  comment_replied: 'replied to a comment on',

  // --- Artifact (existing, previously unlabeled) ---
  artifact_created: 'created',
  artifact_deleted: 'deleted',
  artifact_submitted_for_review: 'submitted for review',
  artifact_review_approved: 'approved',
  artifact_review_changes_requested: 'requested changes on',
  artifact_review_cancelled: 'cancelled review of',

  // --- Outline ---
  outline_updated: 'updated the outline of',

  // --- Document ownership (existing, previously unlabeled) ---
  document_owner_assigned: 'was assigned to',
  document_owner_removed: 'was unassigned from',

  // --- Bid mutations (new) ---
  bid_created: 'created this bid',
  bid_updated: 'updated',
  bid_status_changed: 'changed status',
  bid_archived: 'archived this bid',
  bid_unarchived: 'unarchived this bid',
  bid_outcome_recorded: 'recorded the result',
  // R4 stage model. A lifecycle move reads by where it went (see
  // getLifecycleChangeLabel); this is only the fallback.
  bid_stage_changed: 'changed the stage',
  bid_lifecycle_changed: 'moved the bid',

  // --- Document mutations (new) ---
  document_uploaded: 'uploaded',
  document_deleted: 'deleted',
  document_categorized: 'categorised',

  // --- Requirement mutations (new) ---
  requirement_updated: 'updated',
  requirement_deleted: 'deleted a requirement',

  // --- Analysis ---
  tender_analyzed: 'analysed the tender',

  // --- Reserved / unemitted AI-gen events (label map completeness) ---
  requirements_generated: 'generated requirements',
  plan_generated: 'generated a response plan',
  draft_generated: 'generated a draft for',

  // --- Strategy (go/no-go, bid brief, win themes) ---
  go_no_go_decided: 'recorded a go/no-go decision',
  go_no_go_assessment_created: 'started a go/no-go assessment',
  go_no_go_assessment_reopened: 'reopened the go/no-go assessment',
  go_no_go_ai_scores_applied: 'applied AI scores to the go/no-go assessment',
  bid_brief_approved: 'approved the bid brief',
  win_theme_added: 'added the win theme',
  win_theme_removed: 'removed the win theme',
}

/**
 * Rendered after the target name: "<actor> added <target> to the team".
 */
export const ACTION_SUFFIXES: Record<string, string> = {
  member_added: 'to the team',
  member_removed: 'from the team',
}

/**
 * Legacy rows without a target fall back to passive voice.
 */
export const NO_TARGET_FALLBACK_LABELS: Record<string, string> = {
  member_added: 'was added to the team',
  member_removed: 'was removed from the team',
}

/**
 * First-person variants of the no-target fallbacks ("You was" → "You were").
 */
export const NO_TARGET_CURRENT_USER_FALLBACK_LABELS: Record<string, string> = {
  member_added: 'were added to the team',
  member_removed: 'were removed from the team',
}

/**
 * First-person ("You ...") overrides for actions whose third-person past-tense
 * reads ungrammatically with "You" (e.g. "You was assigned" → "You were assigned").
 */
export const CURRENT_USER_ACTION_LABELS: Record<string, string> = {
  section_assigned: 'were assigned',
}

// ===================
// Helpers
// ===================

/**
 * The phrase for a `bid_lifecycle_changed` row, by where the bid went:
 * "moved the bid to Leads", "started bidding at", "reopened the bid at",
 * "marked the bid as submitted", "closed the bid".
 * A move to active ends in "at" only when the row shows a stage pill after it
 * ("reopened the bid at [Submit]"); without a known stage there is no pill.
 */
export function getLifecycleChangeLabel(metadata: Record<string, unknown>): string {
  const from = metadata['from']
  const to = metadata['to']
  switch (to) {
    case 'lead':
      return 'moved the bid to Leads'
    case 'active': {
      const phrase = from === 'lodged' || from === 'closed' ? 'reopened the bid' : 'started bidding'
      return isBidStage(metadata['toStage']) ? `${phrase} at` : phrase
    }
    case 'lodged':
      return 'marked the bid as submitted'
    case 'closed':
      return 'closed the bid'
    default:
      return ACTION_LABELS['bid_lifecycle_changed'] ?? 'moved the bid'
  }
}

/**
 * The phrase for an old-vocabulary `bid_status_changed` row, in the R4 words.
 * Mark as submitted still logs this action (to `submitted`), and history from
 * before R4 is full of it, so it must read like the lifecycle rows do:
 * "marked the bid as submitted", "moved the bid to Write", "recorded the result".
 * Returns null where there is no clear mapping (the caller falls back to
 * "changed status" with the raw before/after pills).
 */
export function getStatusChangeLabel(metadata: Record<string, unknown>): string | null {
  switch (metadata['to']) {
    case 'submitted':
      return 'marked the bid as submitted'
    case 'lead':
      return 'moved the bid to Leads'
    case 'qualifying':
      return 'moved the bid to Qualify'
    case 'drafting':
      return 'moved the bid to Write'
    case 'won':
    case 'lost':
    case 'no_go':
      return ACTION_LABELS['bid_outcome_recorded'] ?? 'recorded the result'
    default:
      return null
  }
}

/** Trailing preposition on a label that expects a target to follow it. */
const TRAILING_PREPOSITION = /\s+(of|on|for|to|from)$/

export function getTargetName(record: ActivityRecord): string | null {
  if (record.targetName) return record.targetName
  const meta = record.metadata
  if (meta['artifactName']) return meta['artifactName'] as string
  if (meta['fileName']) return meta['fileName'] as string
  if (meta['name']) return meta['name'] as string
  if (meta['sectionTitle']) return meta['sectionTitle'] as string
  return null
}

/**
 * Resolve the label for an action in context.
 *
 * Priority:
 *   1. No-target current-user fallback (e.g. "were added to the team")
 *   2. No-target third-person fallback
 *   3. Current-user grammatical override (e.g. "were assigned")
 *   4. Standard label
 *   5. Humanised action key
 */
export function getActionLabel(
  action: string,
  hasTarget: boolean,
  isCurrentUser: boolean,
): string {
  if (!hasTarget) {
    if (isCurrentUser && NO_TARGET_CURRENT_USER_FALLBACK_LABELS[action]) {
      return NO_TARGET_CURRENT_USER_FALLBACK_LABELS[action]
    }
    if (NO_TARGET_FALLBACK_LABELS[action]) {
      return NO_TARGET_FALLBACK_LABELS[action]
    }
  }
  const label =
    isCurrentUser && CURRENT_USER_ACTION_LABELS[action]
      ? CURRENT_USER_ACTION_LABELS[action]
      : (ACTION_LABELS[action] ?? action.replace(/_/g, ' '))

  // Most labels point at a target ("updated the outline of <name>"). With no
  // name to point at, the preposition is left hanging against whatever follows
  // — "You updated the outline of · <bid>". Trimming it leaves a sentence that
  // still reads: "You updated the outline". A backstop, not a substitute for
  // resolving the name server-side.
  return hasTarget ? label : label.replace(TRAILING_PREPOSITION, '')
}

/**
 * Returns the in-app link for an activity record, or null when the row should
 * not be clickable (deleted targets, bid/member targets with no detail page).
 */
export function getActivityLink(
  bidId: string,
  record: Pick<ActivityRecord, 'action' | 'targetType' | 'targetId' | 'metadata'>,
): string | null {
  // Deleted targets have no live page to link to
  if (record.action.endsWith('_deleted')) return null

  const { targetType, targetId, metadata } = record
  const artifactId = metadata['artifactId'] as string | undefined

  if (targetType === 'section' && targetId && artifactId) {
    return `/app/bids/${bidId}/responses/${artifactId}/sections/${targetId}`
  }
  if (targetType === 'artifact' && targetId) {
    return `/app/bids/${bidId}/responses/${targetId}`
  }
  if (targetType === 'document') {
    return `/app/bids/${bidId}/files`
  }
  if (targetType === 'requirement') {
    return `/app/bids/${bidId}/qualify?view=compliance_matrix`
  }
  // Risk and criterion threads land on their own Qualify view with the row
  // named, matching the deep link the comment notification carries. Without the
  // `?view=`/`?item=` pair the row would open under whichever sub-tab the user
  // last left, which for a risk or a criterion is rarely the right one.
  if (targetType === 'risk' && targetId) {
    return `/app/bids/${bidId}/qualify?view=risk_register&item=${targetId}`
  }
  if (targetType === 'criterion' && targetId) {
    return `/app/bids/${bidId}/qualify?view=evaluation_criteria&item=${targetId}`
  }
  // bid / member targets — no dedicated detail link
  return null
}

// ===================
// Time Formatting
// ===================

/**
 * Time of day, for rows that already sit under a day heading. Locale-formatted
 * so it follows the reader's 12/24-hour convention.
 */
export function formatClockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
